/**
 * ClientForm — reusable form for creating and editing clients.
 *
 * Props:
 *   mode     — 'create' or 'edit'
 *   client   — existing client data (ClientRow from API, required in edit mode)
 *   onSubmit — called with form data when user submits
 *   onCancel — called when user cancels
 *
 * In edit mode: shows the current status badge and status transition buttons.
 */

import { useState } from 'react'
import type { ClientRow, CreateClientDTO, UpdateClientDTO } from '@1klick/types'

// ── Status badge ─────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-700',
  suspended: 'bg-red-100 text-red-800',
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Activo',
  inactive: 'Inactivo',
  suspended: 'Suspendido',
}

function StatusBadge({ status }: { status: string }) {
  const color = STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-700'
  const label = STATUS_LABELS[status] ?? status
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${color}`}>
      {label}
    </span>
  )
}

// ── Status transition map (mirrored from API/types) ───────────────────────────

const ALLOWED_TRANSITIONS: Record<string, string[]> = {
  active: ['inactive', 'suspended'],
  inactive: ['active'],
  suspended: ['active', 'inactive'],
}

const TRANSITION_LABELS: Record<string, string> = {
  active: 'Activar',
  inactive: 'Desactivar',
  suspended: 'Suspender',
}

// ── Props ─────────────────────────────────────────────────────────────────────

type FormMode = 'create' | 'edit'

interface ClientFormProps {
  mode: FormMode
  client?: ClientRow
  onSubmit: (data: CreateClientDTO | UpdateClientDTO) => Promise<void>
  onCancel: () => void
  isSubmitting?: boolean
  error?: string | null
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ClientForm({
  mode,
  client,
  onSubmit,
  onCancel,
  isSubmitting = false,
  error = null,
}: ClientFormProps) {
  const [name, setName] = useState(client?.name ?? '')
  const [contactName, setContactName] = useState(client?.contact_name ?? '')
  const [contactEmail, setContactEmail] = useState(client?.contact_email ?? '')
  const [contactPhone, setContactPhone] = useState(client?.contact_phone ?? '')
  const [taxId, setTaxId] = useState(client?.tax_id ?? '')
  const [channel, setChannel] = useState<string>(client?.channel ?? 'direct')
  const [notes, setNotes] = useState(client?.notes ?? '')
  const [statusTransition, setStatusTransition] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const base = {
      name: name.trim(),
      contactName: contactName.trim(),
      contactEmail: contactEmail.trim(),
      contactPhone: contactPhone.trim() || undefined,
      taxId: taxId.trim() || undefined,
      channel: channel as 'direct' | 'reseller' | 'freelance',
      notes: notes.trim() || undefined,
    }

    if (mode === 'edit' && statusTransition) {
      await onSubmit({ ...base, status: statusTransition as 'active' | 'inactive' | 'suspended' })
    } else {
      await onSubmit(base)
    }
  }

  const currentStatus = client?.status ?? 'active'
  const availableTransitions = ALLOWED_TRANSITIONS[currentStatus] ?? []

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Status bar (edit mode only) */}
      {mode === 'edit' && client && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
          <span className="text-sm font-medium text-gray-600">Estado actual:</span>
          <StatusBadge status={currentStatus} />
          {availableTransitions.length > 0 && (
            <div className="ml-auto flex gap-2">
              {availableTransitions.map((target) => (
                <button
                  key={target}
                  type="button"
                  onClick={() => setStatusTransition(statusTransition === target ? null : target)}
                  className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                    statusTransition === target
                      ? 'bg-blue-600 text-white'
                      : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {TRANSITION_LABELS[target] ?? target}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Name */}
      <div>
        <label htmlFor="cf-name" className="mb-1 block text-sm font-medium text-gray-700">
          Nombre <span className="text-red-500">*</span>
        </label>
        <input
          id="cf-name"
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ej. Grupo Takami"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {/* Contact name */}
      <div>
        <label htmlFor="cf-contactName" className="mb-1 block text-sm font-medium text-gray-700">
          Nombre del contacto <span className="text-red-500">*</span>
        </label>
        <input
          id="cf-contactName"
          type="text"
          required
          value={contactName}
          onChange={(e) => setContactName(e.target.value)}
          placeholder="Ej. Carlos Lopez"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {/* Contact email */}
      <div>
        <label htmlFor="cf-contactEmail" className="mb-1 block text-sm font-medium text-gray-700">
          Email del contacto <span className="text-red-500">*</span>
        </label>
        <input
          id="cf-contactEmail"
          type="email"
          required
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
          placeholder="contacto@empresa.com"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {/* Contact phone */}
      <div>
        <label htmlFor="cf-contactPhone" className="mb-1 block text-sm font-medium text-gray-700">
          Telefono del contacto
        </label>
        <input
          id="cf-contactPhone"
          type="tel"
          value={contactPhone ?? ''}
          onChange={(e) => setContactPhone(e.target.value)}
          placeholder="+57 300 000 0000"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {/* Tax ID (NIT) */}
      <div>
        <label htmlFor="cf-taxId" className="mb-1 block text-sm font-medium text-gray-700">
          NIT / RUT
        </label>
        <input
          id="cf-taxId"
          type="text"
          value={taxId ?? ''}
          onChange={(e) => setTaxId(e.target.value)}
          placeholder="900.123.456-7"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {/* Channel */}
      <div>
        <label htmlFor="cf-channel" className="mb-1 block text-sm font-medium text-gray-700">
          Canal de venta
        </label>
        <select
          id="cf-channel"
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="direct">Directo</option>
          <option value="reseller">Revendedor</option>
          <option value="freelance">Freelance</option>
        </select>
      </div>

      {/* Notes */}
      <div>
        <label htmlFor="cf-notes" className="mb-1 block text-sm font-medium text-gray-700">
          Notas
        </label>
        <textarea
          id="cf-notes"
          value={notes ?? ''}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Informacion adicional sobre el cliente..."
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? 'Guardando...' : mode === 'create' ? 'Crear cliente' : 'Guardar cambios'}
        </button>
      </div>
    </form>
  )
}
